/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IPriceComponentResources } from './components/price.component';
import { ProductPrice, ProductSearchResult, ProductWarehouseInventoryAvailability, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    createInventoryAvailabilitySearchCriteria,
    getProductPageUrlSync
} from '@msdyn365-commerce-modules/retail-actions';
import {
    ICategoryHierarchyViewProps,
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISearchResultModalViewProps,
    ISortByViewProps,
    ITitleViewProps
} from '@msdyn365-commerce-modules/search-result-container';
import { Button, getTelemetryObject, IPopupProps, Module, Node, Popup } from '@msdyn365-commerce-modules/utilities';
import React, { PropsWithChildren, useState } from 'react';
import { _getGlobalState } from 'mobx';
import { ISearchResultContainerProps } from '../definition-extensions/search-result-container.ext.props.autogenerated';
import { ProductComponent } from './components/product.component';
import MsDyn365, {
    getUrlSync,
    IGridSettings,
    IImageData,
    IImageProps,
    IImageSettings,
    Image,
    IRequestContext
} from '@msdyn365-commerce/core';
import { AddToCartListComponent } from './components/addToCart.component';
import { IProductSearchResultExtensionData } from './product-result-container.data';
import { getInventoryLevelRangesAsync } from '../../../actions/DataActionExtension.g';
import { SupersessionKey } from '../../gardner-theme/models/SupersessionKey';
import getProductSupersessionProperty from '../../gardner-theme/models/getProductSupersessionProperty';
import { IItemInventoryLevelProfileEntity, IProfileDataMap } from '../../../actions/DataServiceEntities.g';
import Table from 'reactstrap/lib/Table';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
// import { LoginToViewPricingComponent } from "../../../components/LoginToViewPricing.component";
import { Tooltip } from 'reactstrap';
import { buildOutOfStockNotificationUrl } from '../../../utils/BuildOutOfStockNotificationUrl';
import { getFallbackImageSrc, getManufacturerCode } from '../../../utils/ImageFallbackUtil';
import { buildSearchResultsInventoryList } from '../../../utils/searchGTMVariablesHelper';

/**
 * DescriptionInterval.
 */
enum DescriptionInterval {
    productDescriptionInterval = 14,
    productDescriptionIntervalRemainder0 = 0,
    productDescriptionIntervalRemainder9 = 9
}

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProductCards = (
    props: PropsWithChildren<ISearchResultContainerViewProps & ISearchResultContainerProps<{}>>,
    estimatedAvailabilites: ProductWarehouseInventoryAvailability[]
): JSX.Element | null => {
    const products = props.data.listPageState.result?.activeProducts;
    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }
    return (
        <Node {...props.ProductsContainer}>
            {props.errorMessage}
            <ul className='list-unstyled'>
                {products.map((product: ProductSearchResult, index: number) =>
                    renderProductCardsDescription(props, product, index, estimatedAvailabilites, false)
                )}
            </ul>
        </Node>
    );
};

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);

    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }

    return undefined;
}

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @param product - The product item data.
 * @param index - The index of each product item.
 * @param isEnabledProductDescription - The flag for showing Product desription tile.
 * @returns The JSX Element.
 */
const renderProductCardsDescription = (
    props: PropsWithChildren<ISearchResultContainerViewProps & ISearchResultContainerProps<{}>>,
    product: ProductSearchResult,
    index: number,
    estimatedAvailabilites: ProductWarehouseInventoryAvailability[],
    isEnabledProductDescription?: boolean
): JSX.Element | null => {
    const { config, context, resources } = props;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const breadCrumbType = props.context.app.config.breadcrumbType;
    const isAllowBack = props.config.allowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');
    const classname = isEnabledProductDescription
        ? 'ms-product-search-result__item product__description'
        : 'ms-product-search-result__item';
    const quickviewslot = ArrayExtensions.hasElements(props.slots.quickview) ? props.slots.quickview[0] : undefined;
    const channelInventoryConfigurationId = props.data.products.result?.channelInventoryConfigurationId;

    // @ts-ignore
    const isPriceMinMaxFeatureState = props.data.featureState.result?.find(
        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
    );
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator,
        loginToViewPricingText: resources.loginToViewPricing
    };
    return (
        <li className={classname} key={index}>
            <ProductComponent
                context={context}
                telemetryContent={telemetryContent}
                imageSettings={config.imageSettings}
                freePriceText={resources.priceFree}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                ratingAriaLabel={resources.ratingAriaLabel}
                allowBack={isAllowBack}
                id={props.id}
                typeName={props.typeName}
                data={{ product }}
                quickViewButton={quickviewslot}
                isEnabledProductDescription={isEnabledProductDescription}
                inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                priceResources={priceResources}
                estimatedAvailabilites={estimatedAvailabilites}
                isDoNotAllowBackorder={props.context.app.config.doNotAllowBackOrder || false}
            />
        </li>
    );
};

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProducts = (
    props: PropsWithChildren<ISearchResultContainerViewProps & ISearchResultContainerProps<{}>>,
    estimatedAvailabilites: ProductWarehouseInventoryAvailability[]
): JSX.Element | null => {
    const products = props.data.listPageState.result?.activeProducts;
    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }
    const productDescriptionInterval: number = DescriptionInterval.productDescriptionInterval;
    const productDescriptionIntervalRemainder0: number = DescriptionInterval.productDescriptionIntervalRemainder0;
    const productDescriptionIntervalRemainder9: number = DescriptionInterval.productDescriptionIntervalRemainder9;
    return (
        <Node {...props.ProductsContainer}>
            {props.errorMessage}
            <ul className='list-unstyled'>
                {products.map((product: ProductSearchResult, index: number) =>
                    index % productDescriptionInterval === productDescriptionIntervalRemainder0 ||
                    index % productDescriptionInterval === productDescriptionIntervalRemainder9
                        ? renderProductCardsDescription(props, product, index, estimatedAvailabilites, true)
                        : renderProductCardsDescription(props, product, index, estimatedAvailabilites, false)
                )}
            </ul>
        </Node>
    );
};

/**
 * Render Title Count.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitleCount = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    return <Node {...TitleContainer}>{title.titleCount}</Node>;
};

/**
 * Render Category Hierarchy.
 * @param props - The category hierarchy view props.
 * @returns The JSX Element.
 */
const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {categoryHierarchyList && categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    }

    return null;
};

function boNote(text: string | undefined, inventoryCode: string, RecordId: number) {
    const [isToolTipOpen, setIsToolTipOpen] = React.useState<boolean>(false);
    const toggleTooltip = () => setIsToolTipOpen(!isToolTipOpen);

    if (inventoryCode !== 'BO') {
        return null;
    }

    return (
        <>
            <a
                href='javascript:void(0)'
                className='readMore-toolTip'
                id={`tooltip-${RecordId}`}
                data-toggle='tooltip'
                onClick={toggleTooltip}
            />

            <Tooltip className='qo-tooltip' placement='top' isOpen={isToolTipOpen} target={`tooltip-${RecordId}`} toggle={toggleTooltip}>
                {text}
            </Tooltip>
        </>
    );
}

/**
 * Render the category page items.
 * @param props - The view props.
 * @returns The search Result container module view.
 */
const SearchResultContainerView: React.FC<ISearchResultContainerViewProps &
    ISearchResultContainerProps<IProductSearchResultExtensionData>> = props => {
    const {
        SearchResultContainer,
        pagination,
        ProductSectionContainer,
        choiceSummary,
        isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        categoryHierarchy,
        sortByOptions,
        CategoryNavContainer,
        RefineAndProductSectionContainer,
        FeatureSearchContainer,
        similarLookProduct,
        errorMessage,
        productPrices,
        context: {
            request,
            cultureFormatter,
            actionContext,
            actionContext: {
                requestContext: {
                    gridSettings,
                    user: { isAuthenticated }
                }
            }
        },
        resources: {
            filterText,
            partNoHeading,
            descriptionHeading,
            availabilityHeading,
            quantityHeading,
            // listPriceHeading,
            yourPriceHeading,
            // loginToViewPricing,
            goToCartText,
            continueShoppingText,
            addToCartModalHeading,
            addToCartText
        },
        data: { cart, listPageState: productSearchResults },
        config: { enableProdutDescription, listView, imageSettings }
    } = props;
    React.useEffect(() => {
        if (!MsDyn365.isBrowser) {
            return;
        }

        const totalProductCount = props.data.listPageState?.result?.totalProductCount;
        const productsCount = props.data.products?.result?.count;
        const products = props.data.listPageState?.result?.activeProducts;

        const productCountNumber = Math.max(totalProductCount ?? 0, productsCount ?? 0);

        if (totalProductCount !== undefined || productsCount !== undefined) {
            window.dataLayer = window.dataLayer || [];

            window.dataLayer.push({
                event: 'view_search_results',
                gdn_search_results: productCountNumber,
                gdn_search_results_list: buildSearchResultsInventoryList(products)
            });
        }
    }, [
        request.query,
        props.data.listPageState?.result?.totalProductCount,
        props.data.products?.result?.count,
        props.data.listPageState?.result?.activeProducts
    ]);

    const [areInventoryRangesChecked, setAreInventoryRangesChecked] = useState<boolean>(false);
    const [inventoryRangesList, setInventoryRangesList] = useState<IProfileDataMap[]>([]);
    const [estimatedAvailabilites, setEstimatedAvailabilites] = React.useState<ProductWarehouseInventoryAvailability[]>([]);

    const getInvProfs = async (): Promise<void> => {
        if (areInventoryRangesChecked) {
            return;
        }
        const itemIds = props.data.products?.result?.products.map(p => p.ItemId!) || [];
        const resp = await getInventoryLevelRangesAsync({ callerContext: props.context.actionContext }, itemIds);
        setAreInventoryRangesChecked(true);
        setInventoryRangesList(resp);
    };

    const getEsimatedAvailability = async (): Promise<void> => {
        const itemIds = Array.from(new Set(props.data.products?.result?.products.map((product: any) => product.RecordId)));
        const searchCriteria = createInventoryAvailabilitySearchCriteria(props.context.actionContext, itemIds, true);
        const response = await getEstimatedAvailabilityAsync({ callerContext: props.context.actionContext }, searchCriteria);
        setEstimatedAvailabilites(response.ProductWarehouseInventoryAvailabilities ?? []);
    };

    React.useEffect(() => {
        if (isAuthenticated || !props.config.onlyShowPriceWhenAuthenticated) {
            void getInvProfs();
            void getEsimatedAvailability();
        }
    }, [props.data.products]);

    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const [addedProduct, setAddedProduct] = React.useState<SimpleProduct | undefined>(undefined);
    const [productPricing, setProductPricing] = React.useState<ProductPrice | undefined>(undefined);
    const toggle = () => {
        setIsExpanded(!isExpanded);
    };

    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;
    const isDoNotAllowBackOrder = props.context.app.config.doNotAllowBackOrder || false;
    const oosInvenLabel = props.context.app.config.outOfStockMessage || 'Temporarily out of stock';
    const freePriceMask: string = props.context.app.config.freePriceMask || '----------';

    const setModalProps = (product: SimpleProduct, price: ProductPrice): IPopupProps => {
        return {
            modalOpen: modalOpen,
            context: props.context,
            className: 'list-view__modal',
            gridSettings,
            imageSettings,
            priceComponent: props.component,
            id: '1',
            productQuantity: 1,
            navigationUrl: getUrlSync('cart', actionContext),
            setModalOpen: value => setModalOpen(value),
            typeName: 'Add To Cart',
            data: { product, price },
            dialogStrings: {
                goToCartText: goToCartText,
                currentPriceText: '',
                continueShoppingText: continueShoppingText,
                freePriceText: '',
                headerItemOneText: addToCartModalHeading,
                headerItemFormatText: '',
                originalPriceText: '',
                headerMessageText: ''
            }
        };
    };

    const handleAddToCart = (productId: number, quantity: number) => {
        const foundProduct = productSearchResults.result?.activeProducts.find(pr => pr.RecordId === productId);
        if (!foundProduct) {
            return;
        }
        const product: SimpleProduct = {
            BasePrice: foundProduct.BasePrice || foundProduct.Price,
            AdjustedPrice: foundProduct.Price,
            Name: foundProduct.Name,
            RecordId: foundProduct.RecordId,
            ItemId: foundProduct.ItemId,
            ProductTypeValue: 1,
            Price: foundProduct.Price
        };
        setAddedProduct(product);
        setProductPricing(getProductPrice(productPrices, product.RecordId));
        cart.result
            ?.addProductToCart({
                product,
                count: quantity,
                catalogId: 0
            })
            .then(response => {
                if (response.status === 'SUCCESS' && props.app.config.addToCartBehavior === 'showModal') {
                    setModalOpen(true);
                }
            });
    };

    // const getProductSupersessionProperty = (product: ProductSearchResult, extensionKey: SupersessionKey) => {
    //   const properties = product.ExtensionProperties;
    //   if (properties) {
    //     return properties.find(pr => pr.Key === extensionKey)?.Value?.StringValue || null;
    //   }
    //   return null;
    // }

    const renderAddtoCartButton = (pr: ProductSearchResult) => {
        if (isAuthenticated || !props.config.onlyShowPriceWhenAuthenticated) {
            const currentInvString = getProductSupersessionProperty(pr, SupersessionKey.Inventory) || '0';
            const currentInventory = parseInt(currentInvString, 10);
            const product = estimatedAvailabilites.find(product => product.ProductId === pr.RecordId);
            const invenLevelCode = product?.TotalAvailableInventoryLevelCode || '';

            let OosUpperBound: number = 0;
            if (!isDoNotAllowBackOrder) {
                let invProfOos: IItemInventoryLevelProfileEntity | undefined;
                inventoryRangesList.map(ir => {
                    const tempInvRange = ir.Profile?.find(p => p.ItemId === pr.ItemId && p.Code === 'OOS');
                    if (tempInvRange) {
                        invProfOos = tempInvRange;
                    }
                });

                OosUpperBound =
                    invProfOos?.hasOwnProperty('UpperBoundInclusive') && invProfOos?.UpperBoundInclusive !== undefined
                        ? invProfOos.UpperBoundInclusive
                        : 0;
            }

            const isOutOfStock = currentInventory <= OosUpperBound;

            if (!isOutOfStock) {
                return (
                    <AddToCartListComponent
                        productId={pr.RecordId}
                        onAddToCart={handleAddToCart}
                        addToCartButtonText={addToCartText}
                        isZeroPrice={pr.Price === 0 || pr.BasePrice === 0}
                    />
                );
            }

            const isBackOrderRestricted = isDoNotAllowBackOrder && invenLevelCode === 'BO';
            if (
                isBackOrderRestricted &&
                props.context.app.config.enableOutOfStockNotification &&
                props.context.app.config.outOfStockNotificationUrl
            ) {
                const notifyUrl = buildOutOfStockNotificationUrl(
                    props.context.app.config.outOfStockNotificationUrl,
                    pr.ItemId,
                    props.context.app.config.outOfStockUrlParam1,
                    props.context.app.config.outOfStockUrlParam2
                );

                if (notifyUrl) {
                    const buttonText = props.context.app.config.outOfStockButtonText || 'Notify Me';

                    return (
                        <>
                            <td></td>
                            <td>
                                <a
                                    href={notifyUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='list-view__add-to-cart-btn msc-button list-view__notify-btn'
                                >
                                    {buttonText}
                                </a>
                            </td>
                        </>
                    );
                }
            }
            return (
                <>
                    <td></td>
                    <td>
                        <div className='list-view__prod-unavail'>
                            {isBackOrderRestricted ? oosInvenLabel : props.resources.productUnavailableText}
                        </div>
                    </td>
                </>
            );
        }
        return null;
    };

    const getInventory = (ItemId: number): { Inventorylabel: string; Inventorycode: string } => {
        const product = estimatedAvailabilites.find(product => product.ProductId === ItemId);

        const invenLevelCode = product?.TotalAvailableInventoryLevelCode || '';
        const invenLevelLabel = product?.TotalAvailableInventoryLevelLabel || '';
        if (isDoNotAllowBackOrder && invenLevelCode === 'BO') {
            return {
                Inventorylabel: oosInvenLabel ?? 'Unavailable',
                Inventorycode: 'OOS'
            };
        }

        return {
            Inventorylabel: invenLevelLabel || '',
            Inventorycode: invenLevelCode || ''
        };
    };

    const _renderProductsListView = () => {
        return (
            <Table className={'list-view__table'}>
                <thead>
                    <tr>
                        <th />
                        <th>{partNoHeading}</th>
                        <th>{descriptionHeading}</th>
                        <th>{availabilityHeading}</th>
                        {/* <th>{listPriceHeading}</th> */}
                        <th>{yourPriceHeading}</th>
                        {isAuthenticated || !props.config.onlyShowPriceWhenAuthenticated ? (
                            <>
                                <th>{quantityHeading}</th>
                                <th />
                            </>
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    {productSearchResults.result?.activeProducts.map(pr => {
                        // const pricing = getProductPrice(productPrices, pr.RecordId);
                        // const label = getInventoryLabel(configurationObject?.result?.channelInventoryConfigurationId, pr);
                        // console.log(label);

                        const { Inventorylabel, Inventorycode } = getInventory(pr.RecordId!);

                        // const priceComponent = <span>{cultureFormatter.formatCurrency(pr.Price)}</span>;

                        return (
                            <tr key={pr.RecordId}>
                                <td>
                                    {renderProductPlacementImage(
                                        imageSettings,
                                        gridSettings,
                                        pr.PrimaryImageUrl,
                                        pr.PrimaryImageUrl,
                                        pr.Name,
                                        request,
                                        getManufacturerCode(pr.ItemId)
                                    )}
                                </td>
                                <td>
                                    <a href={getProductPageUrlSync(pr.Name!, pr.RecordId, actionContext)} className='item-id'>
                                        {pr.ItemId}
                                    </a>
                                </td>
                                <td>{pr.Name}</td>
                                <td>
                                    {Inventorylabel} {boNote(props.config.backOrderShippingText, Inventorycode, pr.RecordId)}
                                </td>

                                {/* <td>
                  {LoginToViewPricingComponent(props.context.request, loginToViewPricing,
                    priceComponent, props.config.onlyShowPriceWhenAuthenticated ?? false)}</td> */}
                                <td>
                                    {isAuthenticated || !props.config.onlyShowPriceWhenAuthenticated
                                        ? pr.Price !== 0
                                            ? cultureFormatter.formatCurrency(pr.Price)
                                            : freePriceMask
                                        : null}
                                </td>
                                {renderAddtoCartButton(pr)}
                                {/* {isAuthenticated || !props.config.onlyShowPriceWhenAuthenticated ? (<AddToCartListComponent productId={pr.RecordId} onAddToCart={handleAddToCart}
                  addToCartButtonText={addToCartText} />) : null} */}
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        );
    };

    const showFilterDropdown = props.config.showFilterDropdown;
    const showSortDropdown = props.config.showSortDropdown;

    if (isMobile) {
        return (
            <Module {...SearchResultContainer}>
                {similarLookProduct ? null : (
                    <Node {...CategoryNavContainer}>
                        {renderCategoryHierarchy(categoryHierarchy)}
                        {renderTitle(TitleViewProps)}
                        {renderTitleCount(TitleViewProps)}
                    </Node>
                )}
                {choiceSummary}
                <Node className='msc-sort-filter-wrapper'>{showFilterDropdown && showSortDropdown && modalToggle}</Node>
                {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage)}
                <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                {errorMessage}

                {enableProdutDescription
                    ? renderProducts(props, estimatedAvailabilites)
                    : renderProductCards(props, estimatedAvailabilites)}
                {pagination}
            </Module>
        );
    }

    return (
        <Module {...SearchResultContainer}>
            {/* @ts-ignore */}
            {modalOpen && addedProduct ? <Popup {...setModalProps(addedProduct, productPricing)} /> : null}
            {similarLookProduct ? null : (
                <Node {...CategoryNavContainer}>
                    {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
                    {TitleViewProps && renderTitle(TitleViewProps)}
                    {TitleViewProps && renderTitleCount(TitleViewProps)}
                </Node>
            )}
            <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
            <Node {...RefineAndProductSectionContainer}>
                {(showFilterDropdown || showSortDropdown) && (
                    <Node className='ms-refiner-heading'>
                        <Node className='ms-refiner-heading_title'>
                            {showFilterDropdown && (
                                <Button
                                    className={isExpanded ? 'ms-refine-filter__toggle_expanded' : 'ms-refine-filter__toggle_collapsed'}
                                    aria-label='Filter'
                                    onClick={toggle}
                                    aria-expanded={isExpanded}
                                    tabIndex={0}
                                >
                                    {filterText}
                                </Button>
                            )}
                            <div className='ms-search-result-wrapper-title-choice-summary'>{choiceSummary}</div>
                            {showSortDropdown && (
                                <div className='ms-search-result-wrapper-sort-by-category'>
                                    {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
                                </div>
                            )}
                        </Node>
                        {refineMenu && isExpanded && renderRefiner(refineMenu)}
                    </Node>
                )}
                <Node {...ProductSectionContainer}>
                    {errorMessage}
                    {/*{enableProdutDescription ? renderProducts(props) : renderProductCards(props)}*/}
                    {listView
                        ? _renderProductsListView()
                        : enableProdutDescription
                        ? renderProducts(props, estimatedAvailabilites)
                        : renderProductCards(props, estimatedAvailabilites)}
                    {pagination}
                </Node>
            </Node>
        </Module>
    );
};

/**
 * Render Search result Modal.
 * @param modalProps - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage),
        modalProps.modalFooter
    );
};

/**
 * Render Refiner Mobile.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefinerMobile = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render Search result Body.
 * @param props - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(props.modalBody, {}, renderSort(sortByDropDown, isRecoSearchPage), renderRefinerMobile(refineMenu));
    }
    return null;
};

/**
 * Render Refiner.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefiner = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render sort by drop down.
 * @param props - The sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return <Node {...SortingContainer}>{sortByDropDown}</Node>;
    }
    return null;
};

/**
 * Render Title.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <Node tag='h1' className=''>
                    {title.titlePrefix}
                    {title.titleText}
                </Node>
            </Node>
        );
    }
    return null;
};

const renderProductPlacementImage = (
    productCardimageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    requestContext?: IRequestContext,
    manufacturerCode?: string
): JSX.Element | null => {
    if (!imageUrl || !gridSettings || !productCardimageSettings) {
        return null;
    }
    if (productCardimageSettings) {
        productCardimageSettings.viewports = {
            lg: { h: 100, w: 100, q: '80' },
            md: { h: 100, w: 100, q: '80' },
            sm: { h: 100, w: 100, q: '80' },
            xs: { h: 100, w: 100, q: '80' }
        };
    }
    let manufacturerDefaultImage;

    if (requestContext && manufacturerCode) {
        manufacturerDefaultImage = getFallbackImageSrc(requestContext, manufacturerCode);
    }
    const image: IImageData = {
        src: imageUrl,
        altText: altText ? altText : '',
        fallBackSrc: manufacturerDefaultImage ? manufacturerDefaultImage : fallbackImageUrl
    };
    const imageProps: IImageProps = { gridSettings };

    imageProps.gridSettings = gridSettings;
    imageProps.imageSettings = productCardimageSettings;
    imageProps.imageSettings.cropFocalRegion = true;
    return <Image {...image} {...imageProps} loadFailureBehavior='empty' requestContext={requestContext} className={'eve-listview__img'} />;
};

const getProductPrice = (pricing: ProductPrice[] | undefined, productRecId: number): ProductPrice | undefined => {
    if (!pricing) {
        return undefined;
    }
    return pricing.find(pr => pr.ProductId === productRecId);
};

export default SearchResultContainerView;
